// LIBRERIE/IMPORTS
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import Slider from 'react-slick';
import ReactModal from 'react-modal';
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faMapMarkerAlt, faCamera, faExpand, faMobileAlt, faPhoneVolume, faClose,
    faChevronLeft, faChevronRight, faShareNodes, faEnvelope, faPrint, faCalculator
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp, faFacebook } from "@fortawesome/free-brands-svg-icons";

// HOOKS
import { useGetProperty } from "../../hooks/useGetProperty";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";
import { useSendEmailInfoProperty, useSendEmailMortgage } from "../../hooks/useSendEmail";
import { useAddClickFromSite } from "../../hooks/counters/useAddClickFromSite";

// COMPONENTI
import PropertyDetails from "./PropertyDetails";
import { PropertyGallery } from './Gallery/PropertyGallery';
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import NotFound from "../Error/NotFound";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import MortgageSimulationModal from "./MortgageSimulationModal";
import SeasonalPricesTable from "./SeasonalPricesTable";
import PropertyMap from "./PropertyMap";
import PannellumViewer from "./PanoramicGallery/PannellumViewer";
import PhotoSphereViewer from "./PanoramicGallery/PhotoSphereViewer";

// CONFIGURAZIONI E UTILS
import appConfig, { AGENCY_ID, RESIDENTIAL_CTG_ID, PLOT_CTG_ID, GARAGE_TYPE_ID, DEFAULT_AGENCY_NAME } from "../../config/appConfig";
import { getTipologiaTranslation, LanguageCode } from '../../config/i18n/propertyTranslations';

// Funzioni Helper
const scrollToElement = (event: React.MouseEvent<HTMLAnchorElement>, elementId: string) => {
    event.preventDefault();
    const element = document.getElementById(elementId);
    if (element) {
        element.scrollIntoView({ behavior: "smooth" });
    }
};
const getNestedValue = (obj: any, path: string) => path.split(".").reduce((acc, part) => acc && acc[part], obj);
const decodeHTMLEntities = (text: string) => {
    if (!text) return "";
    try {
        const parser = new DOMParser();
        const decodedString = parser.parseFromString(text, "text/html").body.textContent;
        return decodedString || text;
    } catch (e) {
        console.error("Errore decodifica HTML entities:", e);
        return text;
    }
};

interface PropertyProps { }

const PropertyDetail: React.FC<PropertyProps> = () => {
    const { idSlug } = useParams<{ idSlug: string }>();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const currentLanguage = useMemo(() => {
        const supportedLanguages: ReadonlyArray<LanguageCode> = ['it', 'en', 'de', 'sq', 'ru', 'fr', 'es', 'pt'];

        // Se il multilingua è attivo, proviamo a leggere la lingua dall'URL come prima cosa
        if (appConfig.integrations.multilang) {
            const langFromUrl = location.pathname.split("/").filter(Boolean)[0];
            if ((supportedLanguages as ReadonlyArray<string>).includes(langFromUrl)) {
                return langFromUrl as LanguageCode;
            }
        }

        const i18nLang = i18n.language.split('-')[0];
        if ((supportedLanguages as ReadonlyArray<string>).includes(i18nLang)) {
            return i18nLang as LanguageCode;
        }

        return 'it' as LanguageCode;
    }, [location.pathname, i18n.language]);

    const propertyId = useMemo(() => {
        if (!idSlug) return null;
        const numericId = Number(idSlug.split("-")[0]);
        return isNaN(numericId) ? null : numericId;
    }, [idSlug]);

    // --- Data Fetching ---
    const { propertyDetails, loading: propertyLoading, error: propertyError } = useGetProperty(propertyId);
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(AGENCY_ID);
    const { sendEmail, isLoading: emailLoading, emailError, success: emailSuccess } = useSendEmailInfoProperty();
    const { addClick } = useAddClickFromSite();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // --- Stato Componente ---
    const [clickRegistered, setClickRegistered] = useState(false);
    const [privacyConsent, setPrivacyConsent] = useState(false);
    const [modalPlanimetriaIsOpen, setModalPlanimetriaIsOpen] = useState(false);
    const [isMortgageModalOpen, setIsMortgageModalOpen] = useState(false);
    const [currentPlanimetriaIndex, setCurrentPlanimetriaIndex] = useState(0);

    const {
        sendEmail: sendMortgageEmail,
        isLoading: mortgageEmailLoading,
        emailError: mortgageEmailError,
        success: mortgageEmailSuccess,
        resetSuccessState: resetMortgageSuccess
    } = useSendEmailMortgage();

    // --- Effetti Collaterali ---
    // Registra click
    useEffect(() => {
        if (propertyDetails && !clickRegistered) {
            addClick({ propertyId: propertyDetails.id, agencyId: propertyDetails.agencyId, clickType: 3 });
            setClickRegistered(true);
        }
    }, [propertyDetails, addClick, clickRegistered]);

    // Reindirizzamento a URL canonico (SEF)
    useEffect(() => {
        if (propertyDetails && propertyId && idSlug) {
            const contractSeo = propertyDetails.contractDetails?.contractName?.toLowerCase() ?? 'dettaglio';
            const citySeo = propertyDetails.address?.citySeoString;
            const propertyTypeSeo = propertyDetails.typologyDetails.seoString
            const locationSeoPart = propertyDetails.address?.fractionSeoString ?? propertyDetails.address?.districtSeoString ?? '';
            const expectedSlugContent = `${propertyDetails.id}-${propertyTypeSeo}-${contractSeo}-${citySeo}${locationSeoPart ? '-' + locationSeoPart : ''}`;

            const currentSlugContent = idSlug;

            if (currentSlugContent !== expectedSlugContent) {
                const langPrefix = appConfig.integrations.multilang ? `/${currentLanguage}` : "";
                const expectedUrl = `${langPrefix}/immobile/${expectedSlugContent}`;
                console.warn(`Redirecting to canonical URL. Current: ${location.pathname}, Expected: ${expectedUrl}`);
                navigate(expectedUrl, { replace: true });
            }
        }
    }, [propertyDetails, propertyId, idSlug, navigate, location.pathname, currentLanguage]);


    // --- Handlers Modale Planimetria ---
    const mainPlanimetriaSliderRef = useRef<Slider | null>(null);
    const openPlanimetriaModal = useCallback((index: number) => {
        setCurrentPlanimetriaIndex(index);
        setModalPlanimetriaIsOpen(true);
    }, []);
    const closePlanimetriaModal = useCallback(() => setModalPlanimetriaIsOpen(false), []);
    const nextPlanimetriaImage = useCallback(() => {
        const count = propertyDetails?.planimetry?.length ?? 0;
        if (count > 0) setCurrentPlanimetriaIndex((prev) => (prev + 1) % count);
    }, [propertyDetails]);
    const prevPlanimetriaImage = useCallback(() => {
        const count = propertyDetails?.planimetry?.length ?? 0;
        if (count > 0) setCurrentPlanimetriaIndex((prev) => (prev - 1 + count) % count);
    }, [propertyDetails]);

    // Settings slider planimetria (solo se ci sono planimetrie)
    const planimetriaSettings = useMemo(() => ({
        dots: (propertyDetails?.planimetry?.length ?? 0) > 4,
        infinite: (propertyDetails?.planimetry?.length ?? 0) > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        afterChange: setCurrentPlanimetriaIndex,
    }), [propertyDetails?.planimetry?.length]);

    const planimetriaThumbSettings = useMemo(() => ({
        slidesToShow: Math.min(4, propertyDetails?.planimetry?.length ?? 1),
        slidesToScroll: 1,
        focusOnSelect: true,
        centerMode: false,
        swipeToSlide: true,
        arrows: (propertyDetails?.planimetry?.length ?? 0) > 4,
    }), [propertyDetails?.planimetry?.length]);

    // --- Handler Form Contatti ---
    const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrivacyConsent(e.target.checked);
    };

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        if (!privacyConsent) {
            alert(t('validation.privacyAcceptanceRequired'));
            return;
        }

        if (window._iub && window._iub.cons_instructions) {
            const formElement = document.getElementById("contact_form");
            window._iub.cons_instructions.push(["submit", {
                form: {
                    selector: formElement,
                    map: {
                        subject: {
                            full_name: "info_sender",
                            email: "info_email"
                        },
                        preferences: {
                            privacy_policy: "generic"
                        }
                    },
                    consent: {
                        legal_notices: [{ identifier: "generic" }]
                    }
                }
            }
            ]);
        } else {
            console.warn("Script Iubenda Consent Solution non trovato (window._iub).");
        }

        try {
            if (!executeRecaptcha) {
                console.error("executeRecaptcha not available");
                alert(t('validation.internalErrorRecaptcha'));
                return;
            }
            const recaptchaToken = await executeRecaptcha('contactForm');
            if (!recaptchaToken) {
                console.warn("Token reCAPTCHA non ottenuto");
                alert(t('validation.recaptchaTokenError'));
                return;
            }

            const formData = new FormData(form);
            const result = await sendEmail({
                formData,
                propertyRef: propertyDetails?.ref ?? 'N/D',
                propertyPrice: propertyDetails?.price ?? 0,
                propertyId: propertyDetails?.id ?? 0,
                recaptchaToken: recaptchaToken,
            });

            if (result.success) {
                alert("Richiesta inviata con successo!");
                form.reset();
                setPrivacyConsent(false);
            } else {
                console.error("Fallimento invio email dal backend:", result.error);
                alert("Impossibile inviare la richiesta. Riprova più tardi.");
            }

        } catch (error) {
            console.error("Errore imprevisto durante l'invio del form:", error);
            alert("Si è verificato un errore imprevisto. Riprova più tardi.");
        }

    }, [privacyConsent, executeRecaptcha, sendEmail, propertyDetails, t]);

    // --- Gestione Loading / Errore / Dati Mancanti ---
    if (propertyLoading || agencyLoading) return <LoadingIndicator />;

    if (agencyError) {
        console.error("Errore caricamento dati Proprietà:", agencyError);
        return <NotFound message={t('validation.genericLoadError')} />;
    }

    if (propertyError) {
        console.error("Errore caricamento dati Proprietà:", propertyError);
        return <NotFound message={t('property.notFoundOrInvalid')} />;
    }

    if (!propertyDetails || !propertyId) {
        console.warn("Dettagli proprietà non trovati o ID non valido post-loading.");
        return <NotFound message={t('property.notFoundOrInvalid')} />;
    }

    // Controllo coerenza Agency ID
    const apiAgencyId = propertyDetails.agencyId;
    if (apiAgencyId !== AGENCY_ID) {
        console.error(`Mismatch Agency ID: API=${apiAgencyId}, Config=${AGENCY_ID}`);
        return <NotFound message={t('validation.agencyMismatchError')} />;
    }

    // --- Preparazione Dati Specifici e Testi Tradotti ---

    // Traduzione Tipologia
    const typologyId = propertyDetails.typologyDetails?.typologyId;
    const propertyType = typologyId != null
        ? getTipologiaTranslation(typologyId, currentLanguage)
        : (propertyDetails.typologyDetails?.it ?? t('property.property'));

    // Contratto Tradotto
    const contractKey = `property.contractTypes.${propertyDetails.contractDetails.contractName}`;
    const contractText = t(contractKey, { defaultValue: propertyDetails.contractDetails.contractName });

    // Costruzione Titolo Pagina / Modale
    const locationName = propertyDetails.address?.fractionName || propertyDetails.address?.districtName || propertyDetails.address?.city;
    const fullTitle = `${propertyType} ${t("property.forContract")} ${contractText} ${t("property.inAddress")} ${locationName} (${propertyDetails.address?.provinceAcronym ?? 'N/D'})`;
    const modalTitle = `${propertyType} ${t("property.forContract")} ${contractText.toLowerCase()}, ${t("property.reference")}. ${propertyDetails.ref}`;
    const agencyDisplayName = agency?.name ?? agency?.ragioneSociale ?? DEFAULT_AGENCY_NAME;
    const helmetTitle = t('common.pageTitlePattern', { pageTitle: fullTitle, agencyName: agencyDisplayName });

    // Flags Categoria
    const isResidential = propertyDetails.category === RESIDENTIAL_CTG_ID;
    const isPlot = propertyDetails.category === PLOT_CTG_ID;
    const isGarage = propertyDetails.typologyDetails.typologyId === GARAGE_TYPE_ID;

    // Descrizione formattata e tradotta
    const descriptionRaw = propertyDetails.descriptionDetails
        ?.find(desc => desc.descriptionTypeId === 1 && desc.description && desc.descriptionLang === currentLanguage)
        ?.description
        ?? propertyDetails.descriptionDetails
            ?.find(desc => desc.descriptionTypeId === 1 && desc.description && desc.descriptionLang === 'it')
            ?.description
        ?? "";
    const descriptionDecoded = decodeHTMLEntities(descriptionRaw);
    const formattedDescription = descriptionDecoded.replace(/(\r\n|\n|\r)/g, "<br />");

    const isValidValue = (value: any) => value !== null && value !== 0 && value !== undefined && value !== "N/A" && value !== "n.d." && value !== "";

    // --- Handlers Modale Mutuo ---
    const openMortgageModal = () => {
        resetMortgageSuccess(); // Resetta lo stato in caso di riapertura
        setIsMortgageModalOpen(true);
    }
    const closeMortgageModal = () => setIsMortgageModalOpen(false);

    const handleMortgageSubmit = async (formData: FormData) => {
        if (!executeRecaptcha) {
            console.error("executeRecaptcha not available");
            alert(t('validation.internalErrorRecaptcha'));
            return { success: false };
        }

        try {
            const recaptchaToken = await executeRecaptcha('mortgageForm');
            if (!recaptchaToken) {
                console.warn("Token reCAPTCHA non ottenuto");
                alert(t('validation.recaptchaTokenError'));
                return { success: false };
            }

            return await sendMortgageEmail({
                formData,
                propertyRef: propertyDetails?.ref ?? 'N/D',
                recaptchaToken: recaptchaToken,
            });

        } catch (error) {
            console.error("Errore imprevisto durante l'invio del form mutuo:", error);
            alert("Si è verificato un errore imprevisto. Riprova più tardi.");
            return { success: false };
        }
    };

    const propertyUrl = window.location.href;

    const handlePrint = () => {
        window.print();
    };

    // Testi comuni
    const detailsText = t('property.detailsTitle');
    const descriptionText = t('common.description');
    const planimetryText = t('common.planimetry');
    const planimetriesText = t('common.planimetries');
    const galleryText = t('property.galleryTitle');
    const exclusiveText = t('property.exclusive');
    const negotiableText = t('common.negotiable');
    const referenceText = t('property.reference');
    const nameSurnamePlaceholder = t('contactForm.nameSurname');
    const emailPlaceholder = t('common.email');
    const mobilePlaceholder = t('common.mobile');
    const messagePlaceholder = t('contactForm.message');
    const acceptText = t('contactForm.acceptThe');
    const privacyPolicyText = t('contactForm.privacyPolicyLink');
    const sendingText = t('common.sending');
    const sendMessageText = t('contactForm.sendMessage');
    const successMessage = t('statusMessages.messageSentSuccessfully');
    const whatsappLabel = t('contactForm.whatsappLabel');
    const shareTitle = t('property.shareTitle');
    const shareText = t('property.shareText');
    const printCardTitle = t('property.printCardTitle');
    const mortgageRequestBtnText = t('mortgage.requestBtnText', 'Richiedi Simulazione Mutuo');

    return (
        <>
            <Helmet>
                <title>{helmetTitle}</title>
                <meta name="description" content={`${fullTitle}. ${descriptionDecoded.substring(0, 150)}...`} />
            </Helmet>
            <SubHeaderDefault title={fullTitle} isSeo={true} />

            <section className='space-ptb bg-custom'>
                <div className='container-fluid w-special'>
                    <div className='row'>

                        {/* Colonna Destra: Galleria, Dettagli, Descrizione, Planimetrie */}
                        <div className='col-lg-8'>

                            {/* Galleria Immagini */}
                            <div id="galleria">
                                <PropertyGallery
                                    images={propertyDetails.imagesDetails ?? []}
                                    caption={modalTitle}
                                    hasPlanimetry={(propertyDetails.planimetry?.length ?? 0) > 0}
                                />
                            </div>

                            {/* Sezione Dettagli */}
                            <div className='property-info-detail-wrapper my-5'>
                                <h4>{detailsText}</h4>
                                <PropertyDetails
                                    propertyDetails={propertyDetails}
                                    isResidential={isResidential}
                                    isGarage={isGarage}
                                    isPlot={isPlot}
                                    isValidValue={isValidValue}
                                    getNestedValue={getNestedValue}
                                    decodeHTMLEntities={decodeHTMLEntities}
                                    language={currentLanguage}
                                />
                            </div>

                            {/* Sezione Descrizione */}
                            {formattedDescription && (
                                <div className="property-description mb-5">
                                    {appConfig.features.verifiedPropertyBadge.enabled && propertyDetails.verifiedProperty && (
                                        <img
                                            src="https://casagest24.it/media/images/logo_check24_R.png"
                                            alt="Immobile Verificato CasaCheck24"
                                            className="verified-stamp-logo"
                                            title="Questo immobile è stato verificato secondo i nostri standard."
                                        />
                                    )}
                                    <h4>{descriptionText}</h4>
                                    <div className="p-3" dangerouslySetInnerHTML={{ __html: formattedDescription }} />
                                </div>
                            )}

                            {/* Sezione Prezzi Affitti Stagionali */}
                            {appConfig.features.vacationRentals.enabled &&
                                propertyDetails?.seasonalPrices &&
                                propertyDetails.seasonalPrices.length > 0 && (
                                    <SeasonalPricesTable prices={propertyDetails.seasonalPrices} />
                                )
                            }

                            {appConfig.features.panoramicPhotos.pannellum.enabled &&
                                propertyDetails?.panoramicImages &&
                                propertyDetails.panoramicImages.length > 0 && (
                                    <PannellumViewer images={propertyDetails.panoramicImages} />
                                )
                            }

                            {appConfig.features.panoramicPhotos.photoSphere.enabled &&
                                propertyDetails?.panoramicImages &&
                                propertyDetails.panoramicImages.length > 0 && (
                                    <PhotoSphereViewer images={propertyDetails.panoramicImages} />
                                )
                            }

                            {/* Sezione Mappa Posizione Immobile */}
                            {appConfig.features.propertyDetailMap.enabled &&
                                propertyDetails?.address?.latitude &&
                                propertyDetails?.address?.longitude && (
                                    <div className="property-description p-4 pt-5 my-5">
                                        <h4>{t('property.map.positionTitle', 'Posizione sulla mappa')}</h4>
                                        <PropertyMap
                                            lat={propertyDetails.address.latitude}
                                            lng={propertyDetails.address.longitude}
                                        />
                                    </div>
                                )}


                            {/* Sezione Planimetrie */}
                            {(propertyDetails.planimetry?.length ?? 0) > 0 && (
                                <div className="property-floor-plans mb-5" id="planimetrie">
                                    <h4>{planimetriesText}</h4>
                                    <div className="slider-slick-planimetry p-3">
                                        {/* Slider Principale Planimetrie */}
                                        <div className="slider-planimetry-for mb-3">
                                            <Slider {...planimetriaSettings} ref={(slider) => { mainPlanimetriaSliderRef.current = slider; }}>
                                                {propertyDetails.planimetry!.map((plan, index) => (
                                                    <div key={index} className="slider-item text-center">
                                                        <a onClick={() => openPlanimetriaModal(index)} href="#!" className="portfolio-img d-inline-block" title={t('property.viewPlanimetryFullscreen') || 'Visualizza a schermo intero'}>
                                                            <img className="img-fluid" alt={`${planimetryText} ${index + 1}`} src={plan.planimetriaImageUrl} />
                                                        </a>
                                                    </div>
                                                ))}
                                            </Slider>
                                        </div>
                                        {/* Thumbnails Planimetrie (solo se più di una) */}
                                        {(propertyDetails.planimetry!.length > 1) && (
                                            <div className="slider-planimetry-nav">
                                                <Slider {...planimetriaThumbSettings} asNavFor={mainPlanimetriaSliderRef.current ?? undefined}>
                                                    {propertyDetails.planimetry!.map((plan, index) => (
                                                        <div key={index} className="thumbnail-wrapper p-1">
                                                            <img className="img-fluid border rounded" alt={`${t('common.thumbnail')} ${index + 1}`} src={plan.planimetriaImageUrl} style={{ cursor: 'pointer' }} /> {/* Stile e alt */}
                                                        </div>
                                                    ))}
                                                </Slider>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Modale Planimetrie */}
                            {(propertyDetails.planimetry?.length ?? 0) > 0 && (
                                <ReactModal
                                    isOpen={modalPlanimetriaIsOpen}
                                    onRequestClose={closePlanimetriaModal}
                                    contentLabel={t('property.planimetryModalLabel') || 'Visualizzatore Planimetria'}
                                    className="full-screen-modal"
                                    overlayClassName="modal-overlay"
                                    shouldCloseOnOverlayClick={true}
                                    shouldCloseOnEsc={true}
                                    ariaHideApp={false}
                                >
                                    <button className="close-button" onClick={closePlanimetriaModal} aria-label={t('common.close') ?? 'Chiudi'}>
                                        <FontAwesomeIcon icon={faClose} size="lg" />
                                    </button>
                                    {(propertyDetails.planimetry!.length > 1) && (
                                        <>
                                            <button className="nav-button left" onClick={prevPlanimetriaImage} aria-label={t('common.previous') ?? 'Precedente'}
                                                style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faChevronLeft} size="2x" />
                                            </button>
                                            <button className="nav-button right" onClick={nextPlanimetriaImage} aria-label={t('common.next') ?? 'Successivo'}
                                                style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faChevronRight} size="2x" />
                                            </button>
                                        </>
                                    )}
                                    <img
                                        src={propertyDetails.planimetry![currentPlanimetriaIndex].planimetriaImageUrl}
                                        alt={`${planimetryText} ${currentPlanimetriaIndex + 1}`}
                                        className="modal-img"
                                    />
                                    {/* Barra inferiore con titolo e contatore */}
                                    <div className="mfp-bottom-bar">
                                        <div className="mfp-title">{modalTitle}</div>
                                        <div className="mfp-counter">
                                            {t('property.modalCounterPattern', { current: currentPlanimetriaIndex + 1, total: propertyDetails.planimetry!.length })}
                                        </div>
                                    </div>
                                    <figcaption className="mfp-bottom-bar" style={{ paddingLeft: "1%" }}>
                                        {modalTitle && <div className="mfp-title">{modalTitle}</div>}
                                        {propertyDetails.planimetry!.length > 0 && <div className="mfp-counter" style={{ paddingRight: "1%" }}>{currentPlanimetriaIndex + 1}</div>}
                                    </figcaption>
                                </ReactModal>
                            )}
                        </div>

                        <div className='col-lg-4 mb-5 mb-lg-0'>
                            <div className='sticky-top' style={{ top: '25px' }}>
                                <div className='property-detail-info mb-4'>
                                    <h3 className="h4 mb-1">{propertyType} {t("property.inAddress")} {locationName}</h3>
                                    {/* Indirizzo completo */}
                                    <p className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
                                        {`${locationName} (${propertyDetails.address?.provinceAcronym ?? ''})`}
                                    </p>
                                    {/* Badges */}
                                    <div className="etichette mb-2">
                                        {propertyDetails.exclusive && <span className="badge badge-md badge-esclusiva mr-2">{exclusiveText}</span>}
                                        <span className="badge badge-md badge-primary">{contractText}</span>
                                        {propertyDetails.auctionDetails.auction && (
                                            <span className="badge badge-md badge-primary ml-2">{t('property.auction')}</span>
                                        )}
                                    </div>
                                    {/* Prezzo */}
                                    <span className="price font-xll text-gold d-block">
                                        {propertyDetails.reservedNegotiation
                                            ? t('property.priceReserved')
                                            : propertyDetails.auctionDetails.auction
                                                ? `da € ${new Intl.NumberFormat("it-IT").format(propertyDetails.price)}`
                                                : `€ ${new Intl.NumberFormat("it-IT").format(propertyDetails.price)}${propertyDetails.negotiable ? ` ${negotiableText}` : ""}`
                                        }
                                    </span>
                                    {/* Riferimento */}
                                    <span className="sub-price font-lg d-block mb-3">
                                        <b>{referenceText}: </b> {propertyDetails.ref}
                                    </span>
                                    {/* Pulsante Richiesta Mutuo */}
                                    {appConfig.features.mortgageSimulation.enabled && (
                                        <div className="mortgage-request-wrapper pt-3 pb-4">
                                            <button onClick={openMortgageModal} className="btn btn-primary btn-block"> {/* <-- CLASSE MODIFICATA */}
                                                <span>
                                                    <FontAwesomeIcon icon={faCalculator} className="mr-2" />
                                                    {mortgageRequestBtnText}
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                    {/* Link Ancore Interne */}
                                    <div className="property-detail-quick-links">
                                        <ul className="property-detail-meta list-unstyled grow my-3">
                                            <li className="">
                                                <a href="#galleria" className="smooth-scrolling text" title={galleryText} onClick={(event) => scrollToElement(event, "galleria")}>
                                                    <FontAwesomeIcon icon={faCamera} />
                                                </a>
                                            </li>
                                            {(propertyDetails.planimetry?.length ?? 0) > 0 && (
                                                <li>
                                                    <a href="#planimetrie" className="smooth-scrolling" title={planimetryText} onClick={(event) => scrollToElement(event, "planimetrie")}>
                                                        <FontAwesomeIcon icon={faExpand} />
                                                    </a>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                    {/* Blocco Condivisione */}
                                    {appConfig.features.propertySharing.enabled && (
                                        <div className="property-share-links pt-4 border-top">
                                            <h6 className="mb-3">{shareTitle} <FontAwesomeIcon icon={faShareNodes} className="ml-2 text-primary" /></h6>
                                            <ul className="list-inline">
                                                {/* WhatsApp */}
                                                {appConfig.features.propertySharing.whatsapp && (
                                                    <li className="list-inline-item">
                                                        <a
                                                            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}: ${propertyUrl}`)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-icon-share btn-outline-success"
                                                            title={t('property.shareOnWhatsApp') || 'Condividi su WhatsApp'}
                                                        >
                                                            <FontAwesomeIcon icon={faWhatsapp} />
                                                        </a>
                                                    </li>
                                                )}
                                                {/* Facebook */}
                                                {appConfig.features.propertySharing.facebook && (
                                                    <li className="list-inline-item">
                                                        <a
                                                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(propertyUrl)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-icon-share btn-outline-primary"
                                                            title={t('property.shareOnFacebook') || 'Condividi su Facebook'}
                                                        >
                                                            <FontAwesomeIcon icon={faFacebook} />
                                                        </a>
                                                    </li>
                                                )}
                                                {/* Email */}
                                                {appConfig.features.propertySharing.email && (
                                                    <li className="list-inline-item">
                                                        <a
                                                            href={`mailto:?subject=${encodeURIComponent(fullTitle)}&body=${encodeURIComponent(`${shareText}:\n${propertyUrl}`)}`}
                                                            className="btn btn-icon-share btn-outline-dark"
                                                            title={t('property.shareViaEmail') || 'Condividi via Email'}
                                                        >
                                                            <FontAwesomeIcon icon={faEnvelope} />
                                                        </a>
                                                    </li>
                                                )}
                                                {/* Stampa */}
                                                {appConfig.features.printablePropertyCard.enabled && (
                                                    <li className="list-inline-item">
                                                        <button
                                                            onClick={handlePrint}
                                                            className="btn btn-icon-share btn-outline-secondary"
                                                            title={printCardTitle}
                                                        >
                                                            <FontAwesomeIcon icon={faPrint} />
                                                        </button>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Box Contatti Agenzia */}
                                <div className='agent-contact-inner'>
                                    <div className='d-flex align-items-center mb-3 justify-content-center'>
                                        <img src="/assets/images/logo.png" alt={t('header:navbar.logoAlt') ?? ''} className='img-fluid logo-light' />
                                    </div>
                                    {/* Numeri Telefono */}
                                    <div className='d-flex flex-column align-items-center justify-content-center sidebar-contact text-center mb-3'>
                                        {/* Telefono 1 */}
                                        {(appConfig.agency.customPhone || agency?.phone1) && appConfig.ui.agencyWidget.showPhone && (
                                            <a
                                                className='link-green mb-2'
                                                href={`tel:+39${appConfig.agency.customPhone || agency?.phone1}`}
                                            >
                                                <FontAwesomeIcon icon={faPhoneVolume} className="mr-1" />
                                                {appConfig.agency.customPhone || agency?.phone1}
                                            </a>
                                        )}

                                        {/* Telefono 2 */}
                                        {(appConfig.agency.customPhone2 || agency?.phone2) && appConfig.ui.agencyWidget.showPhone && (
                                            <a
                                                className='link-green mb-2'
                                                href={`tel:+39${appConfig.agency.customPhone2 || agency?.phone2}`}
                                            >
                                                <FontAwesomeIcon icon={faPhoneVolume} className="mr-1" />
                                                {appConfig.agency.customPhone2 || agency?.phone2}
                                            </a>
                                        )}

                                        {/* Mobile */}
                                        {agency?.mobile && appConfig.ui.agencyWidget.showMobile && (
                                            <a className='link-green mb-2' href={`tel:+39${agency.mobile}`}>
                                                <FontAwesomeIcon icon={faMobileAlt} className="mr-1" /> {agency.mobile}
                                            </a>
                                        )}

                                        {/* WhatsApp */}
                                        {appConfig.socialLinks.whatsapp && appConfig.ui.agencyWidget.showSocialLinks && (
                                            <a className='link-green mb-2' href={appConfig.socialLinks.whatsapp}>
                                                <FontAwesomeIcon icon={faWhatsapp} className="mr-1" /> {whatsappLabel}
                                            </a>
                                        )}
                                    </div>

                                    {/* Form */}
                                    <form onSubmit={handleSubmit} id='contact_form' name='contact_form' noValidate>
                                        <div className='form-row'>
                                            <div className='form-group col-12'>
                                                <input type="text" id="contact_sender" name="info_sender" className="form-control form-control-sm" placeholder={nameSurnamePlaceholder} required aria-label={nameSurnamePlaceholder} />
                                            </div>
                                            <div className='form-group col-md-6'>
                                                <input type="email" id="contact_email" name="info_email" className="form-control form-control-sm" placeholder={emailPlaceholder} required aria-label={emailPlaceholder} />
                                            </div>
                                            <div className='form-group col-md-6'>
                                                <input type="tel" id="contact_tel" name="info_tel" className="form-control form-control-sm" placeholder={mobilePlaceholder} required aria-label={mobilePlaceholder} />
                                            </div>
                                            <div className='form-group col-12'>
                                                <textarea id="contact_message" name="info_message" rows={4} className="form-control form-control-sm" placeholder={messagePlaceholder} required aria-label={messagePlaceholder} />
                                            </div>
                                            <div className='form-group col-12'>
                                                <div className="custom-control custom-checkbox small">
                                                    <input type="checkbox" id="contact_privacy" name="contact_privacy" value="1" className="custom-control-input" required checked={privacyConsent} onChange={handlePrivacyChange} aria-describedby="privacyHelp" />
                                                    <label htmlFor="contact_privacy" className="custom-control-label">
                                                        {acceptText}
                                                        <a href={`${appConfig.integrations.multilang ? `/${currentLanguage}` : ''}/privacy`} target="_blank" rel="noopener noreferrer" className="ml-1">{privacyPolicyText}</a>.
                                                    </label>
                                                    <small id="privacyHelp" className="form-text">{t('validation.privacyAcceptanceRequired')}</small>
                                                </div>
                                            </div>
                                            <div className='col-12'>
                                                <button type="submit" disabled={emailLoading} className="btn btn-primary btn-block">
                                                    <span>{emailLoading ? sendingText : sendMessageText}</span>
                                                </button>
                                            </div>
                                        </div>
                                        {/* Messaggio Successo/Errore */}
                                        {emailSuccess && <p className="mt-3 text-center text-success small">{successMessage}</p>}
                                        {emailError && <p className="mt-3 text-center text-danger small">{t('validation.emailSendError', { error: emailError })}</p>}
                                    </form>
                                </div>
                            </div>
                        </div>

                        {appConfig.features.mortgageSimulation.enabled && (
                            <MortgageSimulationModal
                                isOpen={isMortgageModalOpen}
                                onRequestClose={closeMortgageModal}
                                propertyPrice={propertyDetails.price}
                                onSubmit={handleMortgageSubmit}
                                isLoading={mortgageEmailLoading}
                                error={mortgageEmailError}
                                success={mortgageEmailSuccess}
                            />
                        )}

                    </div>
                </div>
            </section >
        </>
    );
};

export default PropertyDetail;